<template>
  <ion-page :class="{ 'disabled-clicks': loading }">
    <ion-header v-if="isLogged" mode="ios">
      <ion-toolbar
        :style="{
          '--background': primaryColor,
          '--border-width': '0px',
        }"
      >
        <ion-buttons>
          <IonBackButton text="" default-href="/main/home" color="light" />
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <AppHeaderDownloadApp />

    <ion-content :style="getBackgroundColor">
      <ion-grid>
        <ion-row class="content-wrap">
          <!-- Image -->
          <ion-col
            :size="isMobileScreen ? 4 : 3"
            class="is-flex is-justify-content-center is-flex-direction-column ion-align-items-end"
            :class="{ 'pr-10': !isMobileScreen }"
          >
            <AppImg
              :img-src="banner?.url"
              img-alt="banner"
              class="img-wrap"
              border-radius="10px"
            />

            <div
              v-if="!isMobileScreen"
              class="is-flex is-justify-content-center ion-align-items-center mt-2"
            >
              <ShareButton
                class="mr-3"
                :text="courseData.course_title"
                :course-id="courseData.id"
              />
              <FollowSeriesButton
                :course-id="courseData.id"
                :entity-type="courseData.entity_type"
              />
            </div>
          </ion-col>

          <!-- Info -->
          <ion-col size="8" class="is-flex is-flex-direction-column">
            <h1>{{ courseData.course_title }}</h1>

            <div class="is-flex ion-align-items-center mb-5">
              <h2 class="pointer" @click="openCompanyDetailsPage">
                {{ courseData.company.name }}
              </h2>
            </div>

            <div
              v-if="isMobileScreen"
              class="content-wrap__actions is-flex ion-justify-content-between ion-align-items-center"
            >
              <FollowSeriesButton
                :course-id="courseData.id"
                :entity-type="courseData.entity_type"
              />

              <ShareButton
                class="mr-3"
                :text="courseData.course_title"
                :course-id="courseData.id"
              />
            </div>

            <template v-if="!isMobileScreen">
              <h3>{{ t('podcast.information') }}</h3>
              <p v-html="courseIntroduction"></p>
            </template>
          </ion-col>

          <ion-col v-if="isMobileScreen">
            <p v-html="displayDescription"></p>
            <p
              v-if="descriptionLength > maxDescriptionLength"
              class="ion-text-right pointer has-text-weight-bold"
              @click="isReadMore = !isReadMore"
            >
              {{ readMore }}
            </p>
          </ion-col>

          <!-- Podcasts -->
          <ion-col size="12" size-md="8" offset-md="3">
            <div class="content-wrap__podcasts">
              <ion-accordion-group value="ion-accordion-0" multiple>
                <ion-accordion
                  v-for="(child, index) in sortedCourseData?.children"
                  :key="index"
                  :value="`ion-accordion-${index}`"
                  class="ion-margin-bottom"
                  :style="{ background: 'transparent' }"
                >
                  <ion-item
                    slot="header"
                    color="dark"
                    :style="{
                      '--ion-color-dark': secondaryColor,
                    }"
                  >
                    <ion-label class="content-wrap__podcasts__title">{{
                      child.title || child.course_title
                    }}</ion-label>
                  </ion-item>

                  <div class="mt-5" slot="content">
                    <!-- Sorting -->
                    <div class="ion-text-end">
                      <ion-button
                        fill="text"
                        @click="oldestFirst = !oldestFirst"
                      >
                        <ion-icon
                          slot="end"
                          :icon="oldestFirst ? chevronUp : chevronDown"
                        />
                        {{
                          t(
                            `podcast.${oldestFirst ? 'oldestFirst' : 'newestFirst'}`,
                          )
                        }}
                      </ion-button>
                    </div>

                    <!-- Lazy Load Episodes -->
                    <template
                      v-for="(episode, i) in visibleEpisodes[child.id]"
                      :key="i"
                    >
                      <SinglePodcast
                        v-for="(stream, index) in episode.streams"
                        class="mb-4"
                        :key="`stream-filtered-${index}`"
                        :podcast-data="stream"
                        :course-media="episode.media"
                        :style="{
                          backgroundColor: secondaryColor,
                        }"
                        @click="openPlayer(stream, episode, courseData)"
                      />
                    </template>

                    <!-- Show message if episode has no streams -->
                    <ion-label
                      v-if="
                        !visibleEpisodes[child.id]?.some(
                          (e) => e.streams.length,
                        )
                      "
                      class="content-wrap__podcasts__label"
                      >{{ $t('podcast.noStreams') }}</ion-label
                    >
                  </div>
                </ion-accordion>

                <!-- Infinite Scroll for Episodes -->
                <ion-infinite-scroll @ionInfinite="loadMoreEpisodes">
                  <ion-infinite-scroll-content
                    loading-text="Loading more episodes..."
                  />
                </ion-infinite-scroll>
              </ion-accordion-group>
            </div>
          </ion-col>
        </ion-row>
      </ion-grid>
    </ion-content>
  </ion-page>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import SinglePodcast from '@/components/common/SinglePodcast';
import AppHeaderDownloadApp from '@/components/common/AppHeaderDownloadApp';
import AppImg from '@/components/common/AppImg.vue';
import ShareButton from '@/components/common/ShareButton.vue';
import FollowSeriesButton from '@/components/common/FollowSeriesButton.vue';

import {
  IonContent,
  IonPage,
  IonGrid,
  IonCol,
  IonRow,
  IonAccordionGroup,
  IonAccordion,
  IonItem,
  IonLabel,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonButtons,
  IonButton,
  IonIcon,
  IonBackButton,
  IonHeader,
  IonToolbar,
} from '@ionic/vue';

import {
  TRACKING_PUBLIC_COURSE_VIEW,
  TRACKING_COURSE_VIEW,
  TRACKING_MAIL_CONTENT_CLICK,
  CATEGORY_PODCAST_AUDIO,
  DEEP_LINK_REDIRECT_STORAGE_KEY,
} from '@/config/constants';
import { chevronDown, chevronUp } from 'ionicons/icons';

import useAppStorage from '@/composables/useAppStorage';
import useTracking from '@/composables/useTracking';
import useDeviceScreen from '@/composables/useDeviceScreen';
import useBackgroundColor from '@/composables/useBackgroundColor';
import { getSmallestSizeImage } from '@/helpers/functions';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { isMobileScreen } = useDeviceScreen();
const { trackThis } = useTracking();

const isLogged = computed(() => store.getters['authentication/isLogged']);
const courseData = computed(() => store.getters['course/getCourseData']);

const loading = ref(true);
const episodesPerPage = ref(5); // Default value, will be calculated based on screen height
const visibleEpisodes = ref({}); // Store visible episodes for each child

const oldestFirst = ref(true);

const sortedCourseData = computed(() => {
  const data = courseData.value;
  if (!data?.children) return null;

  const sortFn = oldestFirst.value
    ? (a, b) => b.position - a.position
    : (a, b) => a.position - b.position;

  const sortedChildren = data.children.map((item) => ({
    ...item,
    children: [...item.children].sort(sortFn),
  }));

  return { ...data, children: sortedChildren };
});

watch(sortedCourseData, () => {
  if (sortedCourseData.value) {
    initializeEpisodes();
  }
});

// Calculate episodes per page based on screen height
const calculateEpisodesPerPage = () => {
  const singleEpisodeHeight = isMobileScreen.value ? 70 : 100;
  const viewportHeight = window.innerHeight;

  // Subtract some space for headers, padding, etc. (roughly 300px)
  const availableHeight = viewportHeight - 300;

  const calculatedEpisodes = Math.max(
    5,
    Math.floor(availableHeight / singleEpisodeHeight),
  );

  episodesPerPage.value = calculatedEpisodes;
};

function getEpisodesWithStreams(episodes) {
  return episodes.filter(({ streams }) => streams.length > 0);
}

// Initialize with first batch of episodes
const initializeEpisodes = () => {
  calculateEpisodesPerPage();
  sortedCourseData.value?.children.forEach((child) => {
    visibleEpisodes.value[child.id] = getEpisodesWithStreams(
      child.children,
    ).slice(0, episodesPerPage.value);
  });
};

// Function to load more episodes dynamically
const loadMoreEpisodes = async (event) => {
  sortedCourseData.value.children.forEach((child) => {
    const currentLength = visibleEpisodes.value[child.id].length;
    if (currentLength < child.children.length) {
      visibleEpisodes.value[child.id] = getEpisodesWithStreams(
        child.children,
      ).slice(0, currentLength + episodesPerPage.value);
    }
  });
  event.target.complete();
};

const banner = computed(() => {
  const media =
    getSmallestSizeImage(
      courseData.value.media?.filter(
        (m) => m.category !== CATEGORY_PODCAST_AUDIO,
      ),
      1,
    ) ?? null;
  return media;
});

// Replace links in the introduction with <a> tag only if it's from medischescholing.nl
const courseIntroduction = computed(() => {
  const text = courseData.value.course_introduction || '';

  const urlRegex = /(https?:\/\/[^\s]+)/g;

  return text.replace(urlRegex, (url) => {
    if (url.includes('https://medischescholing.nl')) {
      return `<a href="${url}" target="_blank">${url}</a>`;
    } else {
      return url;
    }
  });
});

const isReadMore = ref(true);
const readMore = computed(() =>
  isReadMore.value ? t('podcast.readMore') : t('podcast.readLess'),
);

const maxDescriptionLength = 200;
const descriptionLength = computed(
  () => courseData.value.course_introduction?.length,
);

const shortSeriesDescription = computed(() => {
  const rawText = courseData.value.course_introduction || '';

  const match = rawText.match(/https:\/\/medischescholing\.nl[^\s]*/);
  const link = match?.[0];
  const linkIndex = match ? rawText.indexOf(link) : -1;

  if (link && linkIndex < maxDescriptionLength) {
    const beforeLink = rawText.substring(0, linkIndex);
    const afterLink = rawText.substring(linkIndex + link.length);

    // Calculate how much space is left after adding before + link
    const remainingChars =
      maxDescriptionLength - (beforeLink.length + link.length);
    const afterLinkTrimmed =
      remainingChars > 0 ? afterLink.substring(0, remainingChars) : '';

    const shouldEllipsis = afterLink.length > remainingChars;

    return (
      beforeLink +
      `<a href="${link}" target="_blank">${link}</a>` +
      afterLinkTrimmed +
      (shouldEllipsis ? ' ...' : '')
    );
  }

  const cutText = rawText.substring(0, maxDescriptionLength);
  return cutText + ' ...';
});

// Final output depending on isReadMore toggle
const displayDescription = computed(() =>
  isReadMore.value ? shortSeriesDescription.value : courseIntroduction.value,
);

const { getDarkestColors, primaryColor, secondaryColor } = useBackgroundColor();

const getBackgroundColor = computed(() => {
  const palette = banner.value?.vibrant_palette_json;

  const { primary, secondary } = getDarkestColors(palette);
  primaryColor.value = primary;
  secondaryColor.value = secondary;

  return {
    '--background': primary,
  };
});

const openPlayer = (stream, course, series, tracking, skipAutoplay = false) => {
  let startAt = course.streams.findIndex(
    (companyStream) => companyStream.id === stream.id,
  );

  if (startAt < 0) {
    return;
  }

  store.dispatch('player/openPlayer', {
    course,
    series,
    company: course.company,
    playlist: course.streams,
    startAt,
    tracking,
    skipAutoplay,
  });
};

const emailTrackingCheck = () => {
  if (route.query.ui) {
    const meta = {
      url: process.env.VUE_APP_BASE_URL + route.fullPath,
      unique_token: route.query.ui,
    };
    trackThis(TRACKING_MAIL_CONTENT_CLICK, meta);
  }
};

const openCompanyDetailsPage = () => {
  router.push({
    name: 'Company',
    params: {
      companyId: courseData.value.company.id,
    },
  });
};

const fetchPublicData = () => {
  // tracking
  trackThis(TRACKING_PUBLIC_COURSE_VIEW, {}, route.params.courseId);

  const { setKey } = useAppStorage();
  setKey(DEEP_LINK_REDIRECT_STORAGE_KEY, `/course/${route.params.courseId}`);

  store.commit('course/refreshCourseData');
  store.commit('player/setPlayerCollapse', true);
  store
    .dispatch('course/setPublicCourseData', route.params.courseId)
    .then(() => {
      initializeEpisodes();
    })
    .catch((error) => {
      if (error.response.status === 422) {
        router.replace('/login');
      }
      if (error.response.status === 404) {
        router.push('/404');
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

function findStreamAndEpisode() {
  const { entity_type } = courseData.value;

  if (entity_type === 'series') {
    for (const season of courseData.value.children) {
      for (const episode of season.children) {
        const stream = episode.streams.find(({ id }) => id === +route.query.s);
        if (stream) {
          return { stream, episode };
        }
      }
    }
  }

  if (entity_type === 'episode') {
    const stream = courseData.value.streams.find(
      ({ id }) => id === +route.query.s,
    );
    if (stream) {
      return { stream, episode: courseData.value };
    }
  }

  return null;
}

const fetchPrivateData = () => {
  // tracking
  store.state.course.courseTrackingEnabled
    ? trackThis(TRACKING_COURSE_VIEW, {}, route.params.courseId)
    : store.commit('course/setCourseTrackingState', true);

  store.commit('course/refreshCourseData');
  store.commit('player/setPlayerCollapse', true);
  store
    .dispatch('course/setCourseData', route.params.courseId)
    .then(() => {
      initializeEpisodes();

      if (route.query.s) {
        const streamData = findStreamAndEpisode();

        if (streamData) {
          const { stream, episode } = streamData;
          const isSeries = courseData.value.entity_type === 'series';
          const series = isSeries ? courseData.value : undefined;

          openPlayer(stream, episode, series);
        }
      }
    })
    .catch((error) => {
      if (error.response.status === 404) {
        router.push('/404');
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

const fetchCourseData = () => {
  isLogged.value ? fetchPrivateData() : fetchPublicData();

  emailTrackingCheck();
};
fetchCourseData();

onMounted(() => {
  window.addEventListener('resize', initializeEpisodes);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', initializeEpisodes);
});
</script>

<style lang="scss" scoped>
@use '@/theme/custom' as *;

.img-wrap {
  &:deep(img) {
    max-height: 300px;
    height: 100%;
  }
}

.content-wrap {
  h1 {
    font-size: 54px;
    font-weight: 700;
    margin-bottom: 15px;
  }

  h2 {
    color: #d1d1d1;
    font-size: 24px;
    font-weight: 300;
  }

  h3 {
    font-size: 36px;
    font-weight: 600;
    margin-bottom: 15px;
  }

  &__podcasts {
    &__title {
      font-size: 16px;
      font-weight: 500;
    }

    &__label {
      font-size: 18px;
      font-weight: 400;
    }

    ion-accordion-group ion-item {
      border-radius: 10px;
    }
  }

  @media (max-width: $tablet-breakpoint) {
    padding: 0;

    h1 {
      font-size: 24px;
    }

    h2 {
      color: #939393;
      font-size: 14px;
      font-weight: 600;
    }

    h3 {
      font-size: 18px;
    }

    &__actions {
      margin-top: auto;

      &:deep(ion-icon) {
        font-size: 24px;
      }
    }

    &__podcasts {
      &__title,
      &__label {
        font-size: 18px;
      }
    }
  }
}

.disabled-clicks {
  pointer-events: none;
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.has-text-weight-bold {
  font-weight: bold;
}
</style>
